"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Sparkles,
  ArrowRight,
  Mic,
  Crown,
  Heart,
  Users,
  Target,
  Trophy,
  Award,
  Network,
  Brain,
  Compass,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TabsBlock } from "@/lib/blocks/tabs-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

export default function SeventyPercentOperatingModelPage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="Glossary · 70% Operating Model"
        overlineClassName="text-emerald"
        title={
          <>
            A Hiring Framework That Produces <span className="text-gradient-premium">Majority-Women Teams</span>
          </>
        }
        subtitle="A team-building and selection framework that optimizes hiring for five operational traits — rigor, communication, risk discipline, retention, and collaboration — without regard to gender. When applied consistently, it produces teams in which women represent approximately seventy percent of the workforce."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Glossary", href: "/glossary/" }, { label: "70% Operating Model" }]}
        badge="Five traits · Empirical outcome"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-5`}>
            <Sparkles className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Definition</span>
          </div>
          <h2 className="text-display-md mb-5">What the 70% Operating Model is.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            <span className="font-semibold text-foreground">The 70% Operating Model</span> is a hiring and team-building framework that selects personnel exclusively on five behavioral traits — rigor, communication, risk discipline, retention, and collaboration — and produces, as an empirical outcome, teams composed of approximately seventy percent women.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The model does not prescribe composition. It prescribes selection criteria. The seventy percent figure is not a target to be met; it is a pattern that emerges when the right signals replace the wrong ones. The framework was developed and validated through a decade of building and running teams across eighteen countries and three continents.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            When these five traits become the selection criteria — assessed through structured interviews, work simulations, and behavioral reference checks — the talent pool that emerges is the one the conventional system overlooks. The operating outcomes are measurably superior. The composition follows.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Target className="size-3.5 text-lime" />
            <span className="text-overline text-lime">The Five Operating Traits</span>
          </div>
          <h2 className="text-display-md mb-4">The selection criteria that produce the outcome.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The five traits are defined in operational terms — measured through structured assessment, not self-reported preference.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { title: "Rigor", body: "The ability to follow protocols without exception, maintaining standards under pressure.", accent: "red" },
            { title: "Communication", body: "The willingness to surface problems early and clearly, before they escalate.", accent: "purple" },
            { title: "Risk Discipline", body: "The capacity to manage risk systematically rather than recklessly.", accent: "cyan" },
            { title: "Retention", body: "The likelihood of staying with an organization long enough to build institutional knowledge.", accent: "emerald" },
            { title: "Collaboration", body: "The ability to make the people around a team member better — not merely to perform individually.", accent: "amber-gold" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Trophy className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Evidence</span>
          </div>
          <h2 className="text-display-md mb-4">A decade of operational data.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The 70% Operating Model is grounded in operational outcomes — verified results produced by teams built and run across the most demanding environments on earth.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { metric: "83.3%", label: "Campaign Success Rate", sub: "15 victories in 18 engagements", accent: "red" },
            { metric: "900M+", label: "Voters Influenced", sub: "Across 22 official languages", accent: "purple" },
            { metric: "99.9999%", label: "Operational Uptime", sub: "31.5 seconds max/year", accent: "denim" },
            { metric: "0", label: "Security Incidents", sub: "15+ years clean record", accent: "emerald" },
            { metric: "100%", label: "Client Satisfaction", sub: "300+ elite engagements", accent: "amber-gold" },
            { metric: "18", label: "Countries", sub: "Three continents", accent: "cyan" },
            { metric: "10+", label: "Years of Evidence", sub: "Sovereign-scale delivery", accent: "lime" },
            { metric: "3", label: "Continents", sub: "Africa, Americas, Asia", accent: "coral" },
          ].map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{s.metric}</div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Award className="size-3.5 text-coral" />
            <span className="text-overline text-coral">The Five Operating Advantages</span>
          </div>
          <h2 className="text-display-md mb-4">What the model produces.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The 70% Operating Model produces five structural operating advantages. These are not theoretical propositions — they are observed, documented, and verified.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { title: "Communication", body: "Teams built using the model identify problems earlier. Team members communicate openly, surfacing issues before they become crises.", accent: "red" },
            { title: "Risk Discipline", body: "The teams manage risk more carefully. They follow protocols, verify standards, and maintain discipline under pressure. The near-perfect uptime and zero security record are the result.", accent: "amber-gold" },
            { title: "Collaboration Over Competition", body: "Internal energy flows outward, toward the mission, rather than inward, toward ego. The teams compete against the problem, not against each other.", accent: "emerald" },
            { title: "Retention", body: "Institutional knowledge is preserved. Tenure extends and knowledge accumulates. Every retained team member carries irreplaceable institutional knowledge.", accent: "cyan" },
            { title: "Ego Management", body: "Ego interferes less with execution. Operational systems function as designed, without the distortions that individual ego introduces.", accent: "purple" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{p.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Network className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Related Terms</span>
          </div>
          <h2 className="text-display-md mb-4">The conceptual map.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { term: "The 70% Project", href: "/the-70-percent-project/", desc: "The flagship initiative documenting the operating case.", accent: "lime" },
            { term: "Operational Excellence", href: "/glossary/operational-excellence/", desc: "The engineered system the model produces.", accent: "amber-gold" },
            { term: "Leadership as Diagnosis", href: "/glossary/leadership-as-diagnosis/", desc: "The clinical method the model applies to selection.", accent: "denim" },
            { term: "Penta P's Framework", href: "/glossary/penta-ps-framework/", desc: "The five-domain model the model operates within.", accent: "cyan" },
            { term: "Strategic Sovereignty", href: "/glossary/strategic-sovereignty/", desc: "The integrated discipline the model supports.", accent: "red" },
            { term: "Sovereign Operations Architecture", href: "/glossary/sovereign-operations-architecture/", desc: "The methodology the model applies.", accent: "emerald" },
          ].map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={t.href} className="block h-full group">
                  <div className={`relative glass-card rounded-2xl p-5 h-full bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                    <h3 className={`text-heading-sm mb-2 ${a.text}`}>{t.term}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed mb-3">{t.desc}</p>
                    <span className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                      Read <ArrowRight className="size-3" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Target className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">70% Model Tabs</span>
          </div>
          <h2 className="text-display-md mb-4">Five tabs on the operating model.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The 70% Operating Model is examined through five tabs — the principles, the five traits, the evidence, the advantages, and the implementation. Each tab isolates a specific dimension of the model.
          </p>
        </div>
        <TabsBlock
          tabs={[
            {
              label: "The Five Traits",
              value: "traits",
              content: (
                <div>
                  <h3 className="text-heading-lg mb-3 text-emerald">The Selection Criteria</h3>
                  <p className="text-body-md text-muted-foreground leading-relaxed mb-4">Rigor, communication, risk discipline, retention, and collaboration. The five operating traits are the selection criteria that produce the 70% outcome. When these traits become the assessment standards — measured through structured interviews, work simulations, and behavioral reference checks — the talent pool that emerges is the one conventional systems miss.</p>
                  <p className="text-body-md text-muted-foreground leading-relaxed">The traits are not aspirational. They are operational specifications, assessed through structured evaluation, observed through sustained operational practice, and verified through performance outcomes.</p>
                </div>
              ),
            },
            {
              label: "The Evidence",
              value: "evidence",
              content: (
                <div>
                  <h3 className="text-heading-lg mb-3 text-amber-gold">A Decade of Operational Data</h3>
                  <p className="text-body-md text-muted-foreground leading-relaxed mb-4">The 70% Operating Model is grounded in operational outcomes — verified results produced by teams built and run across the most demanding environments. The 83.3% campaign success rate, the 99.9999% uptime, the 100% client satisfaction, the zero security incidents — these metrics are produced by teams operating under the 70% composition.</p>
                  <p className="text-body-md text-muted-foreground leading-relaxed">The evidence is longitudinal. It spans fifteen-plus years, eighteen countries, three hundred-plus elite engagements, and every operational domain. The pattern is consistent: majority-female teams produce the most reliable outcomes at sovereign scale.</p>
                </div>
              ),
            },
            {
              label: "The Advantages",
              value: "advantages",
              content: (
                <div>
                  <h3 className="text-heading-lg mb-3 text-red">What the Model Produces</h3>
                  <p className="text-body-md text-muted-foreground leading-relaxed mb-4">Five structural operating advantages: communication (problems identified earlier), risk discipline (protocols followed, standards maintained), collaboration over competition (energy flows toward the mission), retention (institutional knowledge preserved), and ego management (operational systems function as designed).</p>
                  <p className="text-body-md text-muted-foreground leading-relaxed">The advantages are not theoretical. They are observed, documented, and verified. The verified outcomes — 100% client satisfaction, 99.9999% uptime, zero security incidents — are the direct products of the structural advantages.</p>
                </div>
              ),
            },
            {
              label: "The Implementation",
              value: "implementation",
              content: (
                <div>
                  <h3 className="text-heading-lg mb-3 text-purple">Engineering the Composition</h3>
                  <p className="text-body-md text-muted-foreground leading-relaxed mb-4">Implementation begins with a structural audit — an honest assessment of how the organization currently defines, recruits, evaluates, and promotes talent. The audit identifies the specific mechanisms that produce the current composition. Selection criteria are then redefined around the five operating traits.</p>
                  <p className="text-body-md text-muted-foreground leading-relaxed">Implementation timeline: meaningful composition shifts within 12-18 months. Operational advantages fully materialize within 24-36 months as team dynamics develop the trust and communication patterns that produce superior outcomes.</p>
                </div>
              ),
            },
            {
              label: "The Objections",
              value: "objections",
              content: (
                <div>
                  <h3 className="text-heading-lg mb-3 text-coral">Addressing the Critique</h3>
                  <p className="text-body-md text-muted-foreground leading-relaxed mb-4">The most persistent criticism is that the 70% Model represents reverse discrimination. The criticism is addressed through operational evidence: 100% client satisfaction, zero security incidents, 83.3% campaign success — outcomes that speak for themselves. The model is not about excluding men. It is about recognizing that a specific composition produces measurably superior outcomes.</p>
                  <p className="text-body-md text-muted-foreground leading-relaxed">The "pipeline" objection is addressed by demonstrating that the pipeline is not empty — it is invisible to organizations that define talent through a single lens. The 70% Model evaluates capability, not pedigree. The talent that emerges is the talent conventional systems miss.</p>
                </div>
              ),
            },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Compass className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Usage Examples</span>
          </div>
          <h2 className="text-display-md mb-4">The model in operational practice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four examples of the 70% Operating Model applied across different organizational contexts. Each example shows the five-trait selection criteria producing the 70% composition.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "Sovereign-Scale Operations", body: "Teams built and run across eighteen countries. Majority-female composition. The verified outcomes — 100% client satisfaction, 83.3% campaign success, zero security incidents — are the direct products of the 70% composition operating at sovereign scale.", accent: "emerald" },
            { title: "Crisis Response Operations", body: "Crisis response teams built under the 70% Model. The 384x to 1,416x speed advantage over traditional approaches, the 95% crisis prediction accuracy, the sub-5-minute response time — these outcomes are produced by majority-female teams.", accent: "amber-gold" },
            { title: "Government and Public Sector", body: "eGovernance deployments serving up to 120 million citizens per installation. The communication patterns, attention to detail, and accountability structures of the 70% composition produce superior citizen service delivery.", accent: "cyan" },
            { title: "Corporate and Fortune 500", body: "Fortune 500 advisory engagements. The 70% Model applied at corporate scale produces the same structural advantages — superior accuracy, accountability, and continuity. The model is not industry-specific.", accent: "purple" },
          ]}
          columns={2}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <BookOpen className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Common Misconceptions</span>
          </div>
          <h2 className="text-display-md mb-4">What the 70% Operating Model is not.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six persistent misconceptions about the 70% Operating Model. The misconceptions are common because the model challenges deeply held assumptions about gender, talent, and leadership.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "Is the 70% Operating Model a quota system?", a: "No. The model does not prescribe composition. It prescribes selection criteria — five operating traits that produce the 70% outcome as an empirical pattern. When the right signals replace the wrong ones, the talent pool that emerges is the one conventional systems overlook." },
            { q: "Does the 70% Model exclude men?", a: "No. Men constitute up to 30% of the workforce and contribute essential capabilities. The distinction lies in intentional composition versus default composition. The 70% composition emerges from the selection criteria, not from exclusionary practices." },
            { q: "Is the 70% Model a diversity initiative?", a: "No. Diversity initiatives are typically driven by compliance frameworks and representation metrics. The 70% Model is an operating strategy validated by sovereign-scale outcomes. The performance advantage is the metric, not the representation percentage." },
            { q: "Can the 70% Model be applied to any industry?", a: "Yes. The model has been applied across sovereign governance, political campaigns, intelligence operations, defense coordination, Fortune-scale crisis management, financial services, healthcare, and technology. The principle is universal." },
            { q: "Is the 70% Model based on advocacy or evidence?", a: "Evidence. The model is grounded in operational outcomes — verified results produced by teams built and run across the most demanding environments on earth. The advocacy is the consequence of the evidence, not the cause." },
            { q: "Does the 70% Model require specific organizational structures?", a: "No. The model integrates with existing organizational structures. It does not require wholesale restructuring. The framework operates as an overlay that modifies recruitment criteria, promotion evaluation, and team assignment decisions. Organizations maintain their operational frameworks while adjusting the composition variable." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-lime/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Heart className="size-10 text-emerald mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage The 70% Project.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The 70% Operating Model is available through advisory engagements, speaking topics, and consulting projects. The talent the world overlooks is the talent that outperforms.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/the-70-percent-project/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Trophy className="size-4" />
                The 70% Project
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Glossary Hub", description: "Ten terms defining sovereign-scale operations.", href: "/glossary/", icon: BookOpen, accent: "cyan" },
          { title: "About Dr. Jyoti Kush", description: "The full story of the operational architect.", href: "/about/", icon: Crown, accent: "red" },
          { title: "FAQ: 70% Project", description: "The flagship initiative in detail.", href: "/faq/70-percent-project/", icon: Heart, accent: "lime" },
          { title: "FAQ: Women's Empowerment", description: "The operating case for women in leadership.", href: "/faq/womens-empowerment/", icon: Users, accent: "coral" },
        ]}
      />
    </>
  );
}
